import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Close as CloseIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  PictureAsPdf as PdfIcon,
  ImageNotSupported as NoImageIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import {
  Invoice,
  InvoiceFilters as IInvoiceFilters,
  CATEGORY_LABELS,
  CATEGORY_COLORS,
  TIPO_DISPLAY_NAMES,
  InvoiceTipo,
} from "../types/invoice";
import { ApiService } from "../services/api";
import { AuthService } from "../services/authService";

interface InvoiceDetailViewerProps {
  open: boolean;
  startInvoiceId: number | null;
  filters: IInvoiceFilters;
  sortOrder: "ASC" | "DESC";
  onClose: () => void;
}

const InvoiceDetailViewer: React.FC<InvoiceDetailViewerProps> = ({
  open,
  startInvoiceId,
  filters,
  sortOrder,
  onClose,
}) => {
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [fileBlobUrl, setFileBlobUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState(false);

  const invoice = allInvoices[currentIndex];

  // Load all invoices matching filters when opened
  useEffect(() => {
    if (!open || startInvoiceId === null) return;

    let mounted = true;
    setLoadingList(true);

    const loadAll = async () => {
      try {
        const response = await ApiService.getInvoices({
          ...filters,
          sort_order: sortOrder,
          page: 1,
          limit: 100 as any, // Backend max is 100 per page
        });
        // Fetch remaining pages if needed
        let invoices = response.data || [];
        const totalPages = response.pagination?.pages || 1;

        if (totalPages > 1) {
          const remaining = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) =>
              ApiService.getInvoices({
                ...filters,
                sort_order: sortOrder,
                page: i + 2,
                limit: 100 as any,
              })
            )
          );
          for (const r of remaining) {
            invoices = invoices.concat(r.data || []);
          }
        }

        if (mounted) {
          setAllInvoices(invoices);
          const idx = invoices.findIndex((inv) => inv.id === startInvoiceId);
          setCurrentIndex(idx >= 0 ? idx : 0);
          setLoadingList(false);
        }
      } catch {
        if (mounted) setLoadingList(false);
      }
    };

    loadAll();
    return () => { mounted = false; };
  }, [open, startInvoiceId, filters, sortOrder]);

  useEffect(() => {
    if (!open || !invoice) return;

    // Cleanup previous blob
    if (fileBlobUrl) {
      URL.revokeObjectURL(fileBlobUrl);
      setFileBlobUrl(null);
    }
    setFileError(false);
    setIsPdf(false);

    if (!invoice.photo_url) {
      setFileLoading(false);
      return;
    }

    let mounted = true;
    setFileLoading(true);

    const loadFile = async () => {
      try {
        const photoUrl = ApiService.getInvoicePhotoUrl(invoice.id);
        const token = AuthService.getToken();
        const response = await fetch(photoUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Failed to fetch");
        const blob = await response.blob();
        const contentType = response.headers.get("content-type") || blob.type;
        if (mounted) {
          setIsPdf(contentType.includes("pdf"));
          setFileBlobUrl(URL.createObjectURL(blob));
          setFileLoading(false);
        }
      } catch {
        if (mounted) {
          setFileError(true);
          setFileLoading(false);
        }
      }
    };

    loadFile();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, open, invoice?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (fileBlobUrl) URL.revokeObjectURL(fileBlobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && currentIndex > 0) {
        setCurrentIndex((i) => i - 1);
      } else if (e.key === "ArrowRight" && currentIndex < allInvoices.length - 1) {
        setCurrentIndex((i) => i + 1);
      } else if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, currentIndex, allInvoices.length, onClose]);

  if (!open) return null;

  if (loadingList) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
          <Box textAlign="center">
            <CircularProgress sx={{ mb: 2 }} />
            <Typography color="text.secondary">A carregar faturas...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!invoice) return null;

  const formatCurrency = (val: unknown) =>
    `${(Number(val) || 0).toFixed(2)} €`;

  const categoryColor =
    CATEGORY_COLORS[invoice.categoria] || CATEGORY_COLORS.regular;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: "85vh" } }}
    >
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              onClick={() => setCurrentIndex((i) => i - 1)}
              disabled={currentIndex === 0}
              size="small"
            >
              <PrevIcon />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              {currentIndex + 1} / {allInvoices.length}
            </Typography>
            <IconButton
              onClick={() => setCurrentIndex((i) => i + 1)}
              disabled={currentIndex === allInvoices.length - 1}
              size="small"
            >
              <NextIcon />
            </IconButton>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Main content: side by side */}
        <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
          {/* Left: Document/Photo */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.100",
              overflow: "hidden",
              p: 2,
            }}
          >
            {fileLoading ? (
              <CircularProgress />
            ) : fileError ? (
              <Alert severity="error" sx={{ m: 2 }}>Erro ao carregar ficheiro</Alert>
            ) : !invoice.photo_url ? (
              <Box sx={{ textAlign: "center", color: "text.disabled" }}>
                <NoImageIcon sx={{ fontSize: 64, mb: 1 }} />
                <Typography>Sem foto/documento</Typography>
              </Box>
            ) : isPdf && fileBlobUrl ? (
              <iframe
                src={fileBlobUrl}
                title="Documento"
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            ) : fileBlobUrl ? (
              <img
                src={fileBlobUrl}
                alt="Foto da fatura"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            ) : null}
          </Box>

          {/* Right: Invoice data */}
          <Box
            sx={{
              width: 360,
              flexShrink: 0,
              overflow: "auto",
              borderLeft: 1,
              borderColor: "divider",
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {formatCurrency(invoice.valor)}
              </Typography>
              <Chip
                label={CATEGORY_LABELS[invoice.categoria]}
                size="small"
                sx={{
                  mt: 1,
                  bgcolor: categoryColor,
                  color: "white",
                  fontWeight: 600,
                }}
              />
              {invoice.tipo && (
                <Chip
                  label={TIPO_DISPLAY_NAMES[invoice.tipo as InvoiceTipo] || invoice.tipo}
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1, ml: 1 }}
                />
              )}
            </Box>

            <Divider />

            <Field label="Data de Emissão" value={
              invoice.data_emissao
                ? dayjs(invoice.data_emissao).format("DD/MM/YYYY")
                : "-"
            } />
            <Field label="Nº Fatura" value={invoice.numero_fatura || "-"} />

            <Divider />

            <Field
              label="Emitente"
              value={invoice.emitente_nome_curto || invoice.emitente_nome || "-"}
              secondary={invoice.nif_emitente}
            />
            <Field
              label="Adquirente"
              value={invoice.adquirente_nome_curto || invoice.adquirente_nome || "-"}
              secondary={invoice.nif_adquirente}
            />

            {invoice.descricao && (
              <>
                <Divider />
                <Field label="Descrição" value={invoice.descricao} />
              </>
            )}

            <Divider />

            <Field
              label="Criada em"
              value={
                invoice.created_at
                  ? dayjs(invoice.created_at).format("DD/MM/YYYY HH:mm")
                  : "-"
              }
            />
            <Typography variant="caption" color="text.disabled">
              ID: {invoice.id}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const Field: React.FC<{ label: string; value: string; secondary?: string }> = ({
  label,
  value,
  secondary,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">{value}</Typography>
    {secondary && (
      <Typography variant="body2" color="text.secondary">
        {secondary}
      </Typography>
    )}
  </Box>
);

export default InvoiceDetailViewer;
